/**
 * AdminLayout — the chrome around the admin screens (the eastview-os `/admin/*`
 * surfaces: Guidelines / Pricing / Market Data / Schemas / Extractors / Graph
 * Subs / Banned Appraisers).
 *
 *   ┌──────────┬───────────────────────────────────────────┐
 *   │ Admin  « │ Admin / Pricing / DSCR Pricing            │ ← breadcrumb, scrolls
 *   │          ├───────────────────────────────────────────┤
 *   │ PRICING… │ DSCR Pricing  RTL & GUC  Pricing Config   │ ← sub-tabs, STICKY
 *   │ ▸ Guide… ├───────────────────────────────────────────┤
 *   │ ▸ Pricin.│ <Outlet/>                                 │
 *   └──────────┴───────────────────────────────────────────┘
 *
 * The section list is a persistent sidebar rather than a breadcrumb dropdown:
 * every destination is visible at once, and the current one is marked in place.
 * It collapses to a 60px icon rail (remembered per partner in localStorage) and
 * becomes an overlay drawer under the mobile breakpoint.
 *
 * SCROLLING lives HERE, in the main column — not in the screens. That's what
 * lets the tab strip pin while the breadcrumb scrolls away, and it gives the
 * screens' own sticky pieces (the "on this page" rail) a scroll container to
 * resolve against. Screens render their content and do not open a scroller of
 * their own; `AdminScreenShell` is built for that.
 *
 * Everything is driven by ADMIN_NAV_ITEMS so this and the top header's "Admin"
 * dropdown can never drift apart.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import KeyboardDoubleArrowLeftRounded from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import MenuRounded from "@mui/icons-material/MenuRounded";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";

import Flx from "../components/layout/Flx";
import Txt from "../components/typography/Txt";
import { partnerStorage } from "../utils/storage/partnerStorage";
import { A, RAIL_W, SIDEBAR_W } from "../screens/admin/shared/adminTokens";
import { ADMIN_NAV_ITEMS, adminNavGroups } from "./adminNav";
import SectionSubTabs from "./SectionSubTabs";

const COLLAPSE_KEY = "adminSidebarCollapsed";
// Matches the drawer breakpoint below; under it the sidebar overlays instead of
// taking a column of its own.
const NARROW = "@media (max-width: 900px)";

/* ─────────────────────────────────── Sidebar ─────────────────────────────── */

const SidebarItem = ({ item, active, collapsed, onSelect }) => {
  const Icon = item.icon;
  const row = (
    <Box
      component="button"
      type="button"
      aria-current={active ? "page" : undefined}
      onClick={() => onSelect(item.to)}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? "center" : "flex-start",
        width: "100%",
        mb: "1px",
        px: collapsed ? 0 : 1.25,
        py: collapsed ? 1.125 : 1,
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        font: "inherit",
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        textAlign: "left",
        whiteSpace: "nowrap",
        color: active ? A.tealDeep : A.ink2,
        bgcolor: active ? A.tealSoft : "transparent",
        transition: "background .13s, color .13s",
        "&:hover": {
          bgcolor: active ? A.tealSoft : A.bg,
          color: active ? A.tealDeep : A.ink,
        },
        "& svg": {
          fontSize: 18,
          flexShrink: 0,
          color: active ? A.tealDark : A.faint,
        },
        "&:hover svg": { color: active ? A.tealDark : A.ink2 },
        // The active marker rides the panel edge, outside the item's padding.
        ...(active && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: -10,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: "0 3px 3px 0",
            bgcolor: A.teal,
          },
        }),
      }}
    >
      {Icon ? <Icon /> : null}
      {collapsed ? null : <span>{item.label}</span>}
    </Box>
  );

  // Collapsed, the icon is the only affordance — the label becomes the tooltip.
  return collapsed ? (
    <Tooltip title={item.label} placement="right" disableInteractive>
      {row}
    </Tooltip>
  ) : (
    row
  );
};

const AdminSidebar = ({ collapsed, onToggle, activeTo, onSelect, drawer }) => (
  <Box
    component="aside"
    aria-label="Admin sections"
    sx={{
      width: collapsed && !drawer ? RAIL_W : SIDEBAR_W,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      bgcolor: A.surface,
      borderRight: `1px solid ${A.line}`,
      transition: "width .22s cubic-bezier(.4,0,.2,1)",
      ...(drawer
        ? { height: "100%", boxShadow: A.shadowLg }
        : { [NARROW]: { display: "none" } }),
    }}
  >
    <Flx
      ac
      g={1.125}
      sx={{
        px: collapsed && !drawer ? 0 : 1.75,
        pt: 1.75,
        pb: 1.25,
        justifyContent: collapsed && !drawer ? "center" : "flex-start",
      }}
    >
      {collapsed && !drawer ? null : (
        <>
          <Flx
            center
            sx={{
              width: 26,
              height: 26,
              borderRadius: "7px",
              flexShrink: 0,
              bgcolor: A.tealSoft,
              color: A.tealDeep,
            }}
          >
            <SettingsOutlined sx={{ fontSize: 15 }} />
          </Flx>
          <Txt sx={{ fontSize: 13, fontWeight: 700, color: A.ink }}>Admin</Txt>
        </>
      )}
      {drawer ? null : (
        <Tooltip title={collapsed ? "Expand" : "Collapse"} placement="right">
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            sx={{
              ml: collapsed ? 0 : "auto",
              color: A.faint,
              borderRadius: "7px",
              transition: "transform .22s",
              transform: collapsed ? "rotate(180deg)" : "none",
              "&:hover": { bgcolor: A.bg, color: A.ink },
            }}
          >
            <KeyboardDoubleArrowLeftRounded sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
    </Flx>

    <Box sx={{ flex: 1, overflowY: "auto", px: 1.25, pb: 1.75 }}>
      {adminNavGroups().map((group, gi) => (
        <Box
          key={group.label}
          sx={{
            mt: gi === 0 ? 0.5 : 1.75,
            // Collapsed there are no group headings, so a hairline does the
            // grouping instead of a label.
            ...(collapsed && !drawer && gi > 0
              ? { mt: 0.75, pt: 0.75, borderTop: `1px solid ${A.line}` }
              : null),
          }}
        >
          {collapsed && !drawer ? null : (
            <Txt
              sx={{
                display: "block",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: A.faint,
                px: 1.25,
                pb: 0.75,
              }}
            >
              {group.label}
            </Txt>
          )}
          {group.items.map((item) => (
            <SidebarItem
              key={item.to}
              item={item}
              active={item.to === activeTo}
              collapsed={collapsed && !drawer}
              onSelect={onSelect}
            />
          ))}
        </Box>
      ))}
    </Box>
  </Box>
);

/* ─────────────────────────────────── Layout ──────────────────────────────── */

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(
    () => partnerStorage.getItem(COLLAPSE_KEY) === "1",
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  const active = useMemo(
    () =>
      ADMIN_NAV_ITEMS.find((i) => location.pathname.startsWith(i.to)) ||
      ADMIN_NAV_ITEMS[0],
    [location.pathname],
  );

  const subTabs = useMemo(() => active.subTabs || [], [active]);

  // Falls back to the first tab so the bare `/admin/pricing` frame (before its
  // redirect lands) still marks one.
  const activeSub = useMemo(
    () => subTabs.find((s) => location.pathname.startsWith(s.to)) || subTabs[0],
    [subTabs, location.pathname],
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      partnerStorage.setItem(COLLAPSE_KEY, prev ? "0" : "1");
      return !prev;
    });
  }, []);

  const go = useCallback(
    (to) => {
      setDrawerOpen(false);
      navigate(to);
    },
    [navigate],
  );

  // The drawer is a route-level overlay; anything that changes the route or
  // presses Escape should put it away.
  useEffect(() => setDrawerOpen(false), [location.pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setDrawerOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        minHeight: 0,
        height: "100%",
        position: "relative",
        bgcolor: A.bg,
      }}
    >
      <AdminSidebar
        collapsed={collapsed}
        onToggle={toggleCollapsed}
        activeTo={active.to}
        onSelect={go}
      />

      {/* Narrow-window overlay drawer — same sidebar, floated over the page. */}
      {drawerOpen ? (
        <>
          <Box
            onClick={() => setDrawerOpen(false)}
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(23,34,43,.35)",
              zIndex: 95,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 96,
              display: "flex",
            }}
          >
            <AdminSidebar
              drawer
              collapsed={false}
              activeTo={active.to}
              onSelect={go}
            />
          </Box>
        </>
      ) : null}

      {/* Main column — and the section's scroll container. */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Flx
          ac
          g={0.875}
          sx={{
            px: 3.25,
            pt: 1.5,
            pb: 1,
            // pb: subTabs.length ? 0 : 1.5,
            bgcolor: A.surface,
            borderBottom: `1px solid ${A.line}`,
            fontSize: 12.5,
            ...(subTabs.length
              ? null
              : { borderBottom: `1px solid ${A.line}` }),
          }}
        >
          <IconButton
            size="small"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open admin menu"
            sx={{
              display: "none",
              [NARROW]: { display: "inline-flex" },
              ml: -0.5,
              mr: 0.25,
              color: A.muted,
            }}
          >
            <MenuRounded sx={{ fontSize: 19 }} />
          </IconButton>
          <Txt sx={{ fontSize: 12.5, color: A.faint }}>Admin</Txt>
          <Txt sx={{ fontSize: 12.5, color: A.lineStrong }}>/</Txt>
          <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: A.ink }}>
            {active.label}
          </Txt>
          {activeSub ? (
            <>
              <Txt sx={{ fontSize: 12.5, color: A.lineStrong }}>/</Txt>
              <Txt sx={{ fontSize: 12.5, color: A.muted }}>
                {activeSub.label}
              </Txt>
            </>
          ) : null}
        </Flx>

        {/* Sub-tabs pin to the top of this column so the switcher stays
            reachable down a long page; the breadcrumb above scrolls away. */}
        <SectionSubTabs tabs={subTabs} activeTo={activeSub?.to} />

        <Outlet />
      </Box>
    </Box>
  );
}
